import { normalizeSummaryMarkdown } from './normalizeSummaryMarkdown';
// Klembord-conversies voor het kopiëren van een verslag naar een EPD.
// Het klembord krijgt twee smaken: text/html (richtvelden) en text/plain
// (platte tekstvelden). Beide zijn afgestemd op EPD-plakgedrag; voor nette
// documenten bestaan de aparte Word- en PDF-exports.

import { collapseListItemBlankLines, markdownToHtml } from './markdownToHtml';

// Verwijder niet-zichtbare metadata die anders mee gekopieerd zou worden:
// structured-field markers (HTML-comment- én bracket-vorm) en HTML-comments
// zoals <!-- colwidth: ... -->. Wordt vóór zowel de platte-tekst- als de
// HTML-conversie toegepast.
export const stripHiddenMarkers = (markdown: string): string =>
    normalizeSummaryMarkdown(markdown)
        .replace(/\[field-(?:start|end):[^\]]*?\]/g, '')
        .replace(/<!--[\s\S]*?-->/g, '');

// Markdown-smaak voor het klembord: opmaak blijft staan, alleen de
// niet-zichtbare metadata gaat eruit. Weggeknipte marker-regels kunnen extra
// lege regels achterlaten; die worden samengevouwen tot één lege regel.
export const summaryMarkdownToClipboardMarkdown = (markdown: string): string =>
    stripHiddenMarkers(markdown)
        .replace(/\n{3,}/g, '\n\n')
        .trim();

// Tabel-scheidingsregel (| --- | --- |) — niet bruikbaar als platte tekst.
const isTableSeparatorRow = (line: string): boolean =>
    /^\s*\|?(?:\s*:?-{2,}:?\s*\|)+\s*:?-{2,}:?\s*\|?\s*$/.test(line);

// Een markdown-tabelregel (begint en eindigt met |).
const isTableRow = (line: string): boolean => /^\s*\|.*\|\s*$/.test(line.trim());

// Tabelregel → tab-gescheiden cellen, zodat het in een EPD als kolommen plakt.
const tableRowToText = (line: string): string =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '')
        .split('|').map((cell) => cell.trim()).join('\t');

export const summaryMarkdownToPlaintext = (markdown: string): string => {
    // Eerst niet-zichtbare metadata strippen die anders mee gekopieerd wordt.
    const withoutMarkers = stripHiddenMarkers(markdown);

    // Markdown-tabellen omzetten naar tab-gescheiden regels en de
    // scheidingsregel (| --- |) weglaten.
    const withoutTables = withoutMarkers.split('\n').reduce<string[]>((acc, line) => {
        if (isTableSeparatorRow(line)) return acc;
        if (isTableRow(line)) { acc.push(tableRowToText(line)); return acc; }
        acc.push(line);
        return acc;
    }, []).join('\n');

    // Blanco regels tussen lijstitems weghalen zodat een lijst strak plakt in
    // een EPD dat elke newline als nieuwe regel telt. De lege regel tussen
    // alinea's blijft juist behouden (zie onderaan).
    const text = collapseListItemBlankLines(withoutTables)
        // Headers: alleen de tekst behouden, geen extra newlines eromheen
        .replace(/^#{1,6}\s+(.+)$/gm, '$1')
        // Blockquotes: alleen de tekst behouden
        .replace(/^\s*>\s?/gm, '')
        // Lists
        .replace(/^(\s*)[*-]\s+(.+)$/gm, '$1• $2')
        .replace(/^(\d+)\.\s+(.+)$/gm, '$1. $2')
        // Bold and italic
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/_(.+?)_/g, '$1')
        // Links
        .replace(/\[(.+?)\]\(.+?\)/g, '$1')
        // Trailing whitespace per regel weghalen
        .replace(/[ \t]+$/gm, '')
        // Maximaal één lege regel achter elkaar. Die ene lege regel tussen
        // alinea's is de enige alinea-scheiding die een plat EPD-veld
        // overhoudt — alle lege regels weghalen plakt alinea's aan elkaar.
        .replace(/\n{3,}/g, '\n\n');

    return text.trim();
};

// Lijstitems (<li>…</li>) → losse regels met een letterlijk opsommingsteken
// of volgnummer, gescheiden door <br>.
const listItemsToLines = (items: string, marker: (index: number) => string): string => {
    const lines: string[] = [];
    items.replace(/<li>([\s\S]*?)<\/li>/g, (_, content: string) => {
        lines.push(`${marker(lines.length)}${content.trim()}`);
        return '';
    });
    return lines.join('<br>\n');
};

// Placeholders (buiten elk normaal tekstbereik) om tabellen en lijst-
// regeleindes te beschermen tijdens de blok-join in de HTML-conversie.
const TABLE_TOKEN = '\u0000';
const LINE_BREAK_TOKEN = '\u0001';

// EPD-richtvelden filteren geplakte HTML doorgaans streng: veel velden laten
// alleen basis-tags als <b>, <i> en <br> door, of strippen álle tags en houden
// enkel de tekst over. Semantische HTML (<ul>, <strong>, <p>) verliest daar de
// opsommingstekens, vetgedrukte tekst en alinea-scheiding (Zorglink-feedback).
// Lever daarom bewust "platte" HTML aan:
// - vet/cursief als <b>/<i>: worden door de meeste plakfilters doorgelaten;
// - koppen als vetgedrukte regel;
// - lijstitems met een letterlijk opsommingsteken of nummer;
// - regeleindes als <br> mét een newline erachter en een lege regel (<br><br>
//   plus \n\n) tussen blokken, zodat ook een filter dat álle tags stript de
//   regel- en alineastructuur uit de kale tekst overhoudt.
// Tabellen blijven echte <table>-elementen; daar bestaat geen plat alternatief
// voor dat in een richtveld beter plakt.
export const summaryMarkdownToClipboardHtml = (markdown: string): string => {
    const semanticHtml = markdownToHtml(stripHiddenMarkers(markdown))
        .replace(/<(\/?)strong>/g, '<$1b>')
        .replace(/<(\/?)em>/g, '<$1i>');

    // Tabellen bevatten interne newlines; tijdelijk vervangen door een
    // placeholder zodat de blok-join hieronder ze niet aanraakt.
    const tables: string[] = [];
    const withoutTables = semanticHtml.replace(/<table>[\s\S]*?<\/table>/g, (table) => {
        tables.push(table);
        return `${TABLE_TOKEN}${tables.length - 1}${TABLE_TOKEN}`;
    });

    // Blok-tags afpellen tot één regel tekst per blok.
    const flattened = withoutTables
        .replace(/<h[1-6]>([\s\S]*?)<\/h[1-6]>/g, '<b>$1</b>')
        .replace(/<blockquote>\s*<p>([\s\S]*?)<\/p>\s*<\/blockquote>/g, '$1')
        .replace(/<ul>\n?([\s\S]*?)<\/ul>/g, (_, items: string) => listItemsToLines(items, () => '• '))
        .replace(/<ol>\n?([\s\S]*?)<\/ol>/g, (_, items: string) => listItemsToLines(items, (index) => `${index + 1}. `))
        .replace(/<p>([\s\S]*?)<\/p>/g, '$1');

    // Regeleindes bínnen een lijst beschermen en daarna elke resterende
    // newline (= blokgrens) omzetten naar een lege regel.
    const joined = flattened
        .replace(/<br>\n/g, LINE_BREAK_TOKEN)
        .replace(/\n+/g, '<br><br>\n\n')
        .replace(new RegExp(LINE_BREAK_TOKEN, 'g'), '<br>\n');

    return joined.replace(
        new RegExp(`${TABLE_TOKEN}(\\d+)${TABLE_TOKEN}`, 'g'),
        (_, index: string) => tables[Number(index)],
    );
};
